import {
  IssueMovedDocument,
  IssueUpdatedDocument,
  IssueReorderedDocument,
  IssueArchivedDocument,
  IssueTrashedDocument,
  IssueRestoredDocument,
  IssueUnarchivedDocument,
  IssueSummaryFragmentDoc,
  type IssueRow,
} from "#app/lib/data";
import {
  defineSubscription,
  type AnySubscriptionRequest,
} from "#app/lib/subscriptions/subscription-provider";
import { recordAutomationTouch } from "#app/lib/automation-touches";
import {
  removeIssueViaClient,
  insertIssueViaClient,
  writeIssueToCacheViaClient,
  resortStageCacheEdges,
} from "./kanban-cache-utils";

// ---------------------------------------------------------------------------
// Board-level realtime — one set of 7 wire subscriptions per workflow,
// registered by the board via useRegisterSubscriptions. The registry dedupes
// with the list surface's subscriptions (same documents/variables): one wire
// sub, both surfaces' handlers.
// ---------------------------------------------------------------------------

export function kanbanBoardSubscriptions(
  workflowId: string,
): AnySubscriptionRequest[] {
  const variables = { workflowId };
  return [
    // Issue moved between stages
    defineSubscription({
      document: IssueMovedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueMoved;
        if (!event) return;
        recordAutomationTouch(event, "moved");
        const { issue, fromStageId, toStageId } = event;
        // Write the full issue to cache so the reference resolves
        writeIssueToCacheViaClient(client, issue);
        removeIssueViaClient(client, fromStageId, issue.issueId);
        insertIssueViaClient(client, toStageId, {
          issueId: issue.issueId,
          sortOrder: issue.sortOrder,
        });
      },
    }),
    // Issue fields updated (name, assignees, dates, etc.)
    defineSubscription({
      document: IssueUpdatedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueUpdated;
        if (!event) return;
        recordAutomationTouch(event, "updated");
        // Check if stageId changed — if so, we need to move the issue between columns
        const cachedIssueId = client.cache.identify({
          __typename: "Issue",
          issueId: event.issue.issueId,
        });
        const cachedIssue =
          cachedIssueId === undefined
            ? null
            : client.cache.readFragment<IssueRow>({
                id: cachedIssueId,
                fragment: IssueSummaryFragmentDoc,
                fragmentName: "IssueSummary",
              });
        const oldStageId = cachedIssue?.stageId;
        const newStageId = event.issue.stageId;

        // Writing the fragment updates all cached fields for this issue in-place
        writeIssueToCacheViaClient(client, event.issue);

        // If the stage changed, move the issue between kanban columns
        if (oldStageId && newStageId && oldStageId !== newStageId) {
          removeIssueViaClient(client, oldStageId, event.issue.issueId);
          insertIssueViaClient(client, newStageId, {
            issueId: event.issue.issueId,
            sortOrder: event.issue.sortOrder,
          });
        }
      },
    }),
    // Issue reordered within a stage
    defineSubscription({
      document: IssueReorderedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueReordered;
        if (!event) return;
        // Update the issue's sortOrder in cache first, then re-sort the column
        writeIssueToCacheViaClient(client, event.issue);
        resortStageCacheEdges(client.cache, event.stageId);
      },
    }),
    // Issue archived — remove from its stage
    defineSubscription({
      document: IssueArchivedDocument,
      variables,
      onData: (client, data) => {
        const issue = data.issueArchived;
        if (!issue) return;
        removeIssueViaClient(client, issue.stageId, issue.issueId);
      },
    }),
    // Issue trashed — remove from its stage
    defineSubscription({
      document: IssueTrashedDocument,
      variables,
      onData: (client, data) => {
        const issue = data.issueTrashed;
        if (!issue) return;
        removeIssueViaClient(client, issue.stageId, issue.issueId);
      },
    }),
    // Issue restored — add back to its stage
    defineSubscription({
      document: IssueRestoredDocument,
      variables,
      onData: (client, data) => {
        const issue = data.issueRestored;
        if (!issue) return;
        writeIssueToCacheViaClient(client, issue);
        insertIssueViaClient(client, issue.stageId, {
          issueId: issue.issueId,
          sortOrder: issue.sortOrder,
        });
      },
    }),
    // Issue unarchived — add back to its stage
    defineSubscription({
      document: IssueUnarchivedDocument,
      variables,
      onData: (client, data) => {
        const issue = data.issueUnarchived;
        if (!issue) return;
        writeIssueToCacheViaClient(client, issue);
        insertIssueViaClient(client, issue.stageId, {
          issueId: issue.issueId,
          sortOrder: issue.sortOrder,
        });
      },
    }),
  ];
}
